import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  exportCallViewsCsv,
  downloadCsvBlob,
  generateCsvFilename,
} from "../services/callViewsApi";
import { CallViewsExportParams } from "../types/call-views.types";

// Mirrors backend throttle: export 10/h (spec AC).
const RATE_LIMIT_MAX = 10;
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000; // 1 hour

interface ExportState {
  exportsInWindow: number;
  windowStart: number | null;
  nextAvailableTime: number | null;
}

export const useCallViewsExport = () => {
  const [exportState, setExportState] = useState<ExportState>({
    exportsInWindow: 0,
    windowStart: null,
    nextAvailableTime: null,
  });

  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  const startCountdown = useCallback((targetTime: number) => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((targetTime - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0) {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        setExportState((prev) => ({
          ...prev,
          exportsInWindow: 0,
          windowStart: null,
          nextAvailableTime: null,
        }));
      }
    };

    updateCountdown();
    countdownRef.current = setInterval(updateCountdown, 1000);
  }, []);

  const isRateLimited = useCallback(() => {
    const now = Date.now();

    if (!exportState.windowStart) {
      return false;
    }

    if (now - exportState.windowStart >= RATE_LIMIT_WINDOW_MS) {
      setExportState({
        exportsInWindow: 0,
        windowStart: null,
        nextAvailableTime: null,
      });
      return false;
    }

    return exportState.exportsInWindow >= RATE_LIMIT_MAX;
  }, [exportState]);

  const recordExport = useCallback(() => {
    const now = Date.now();

    setExportState((prev) => {
      const windowStart =
        prev.windowStart && now - prev.windowStart < RATE_LIMIT_WINDOW_MS
          ? prev.windowStart
          : now;

      const newExportsInWindow =
        prev.windowStart === windowStart ? prev.exportsInWindow + 1 : 1;

      const nextAvailableTime =
        newExportsInWindow >= RATE_LIMIT_MAX
          ? windowStart + RATE_LIMIT_WINDOW_MS
          : null;

      if (nextAvailableTime) {
        startCountdown(nextAvailableTime);
      }

      return {
        exportsInWindow: newExportsInWindow,
        windowStart,
        nextAvailableTime,
      };
    });
  }, [startCountdown]);

  const mutation = useMutation({
    mutationFn: async (params: CallViewsExportParams) => {
      if (isRateLimited()) {
        throw new Error("Rate limit exceeded. Please try again later.");
      }

      const blob = await exportCallViewsCsv(params);
      const filename = generateCsvFilename(params.startDate, params.endDate);
      downloadCsvBlob(blob, filename);

      recordExport();
      return { success: true, filename };
    },
  });

  const exportCsv = useCallback(
    (params: CallViewsExportParams) => {
      mutation.mutate(params);
    },
    [mutation]
  );

  const remainingExports = Math.max(0, RATE_LIMIT_MAX - exportState.exportsInWindow);

  return {
    exportCsv,
    isLoading: mutation.isPending,
    error: mutation.error,
    isRateLimited: isRateLimited(),
    remainingExports,
    countdown,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};

export default useCallViewsExport;
